import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { normalize } from '@/utils';
import useWindowDimensions from '@/hooks/use-window-dimensions';

interface Props {
  index: number;
  name: string;
  src: string;
  width: number;
  numOfLayers: number;
}

const Layer = ({ index, name, src, width, numOfLayers }: Props) => {
  const [isHovering, setIsHovering] = React.useState(false);

  let { width: windowWidth } = useWindowDimensions();
  // For the first render, assume 1000px wide
  if (typeof windowWidth === 'undefined') {
    windowWidth = 1000;
  }

  const OFFSET_BY = Math.min(windowWidth * 0.1, 85);
  const Wrapper = index === 0 ? FirstLayer : StandardLayer;
  const offset = normalize(
    index,
    0,
    numOfLayers - 1,
    OFFSET_BY,
    -OFFSET_BY
  );

  const Label = TopLabel;

  const style = useSpring({
    transform: `translate(${offset}%) skewY(20deg)`,
    opacity: isHovering ? 1 : 0.75,
  });

  const showLabel = isHovering;

  return (
    <Wrapper
      key={name}
      style={{
        width: width,
        ...style,
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocus={() => setIsHovering(true)}
      onBlur={() => setIsHovering(false)}
    >
      <Label style={{ opacity: showLabel ? 1 : 0 }}>
        <LabelText>{name}</LabelText>
      </Label>
      <Image alt="" src={src} />
    </Wrapper>
  );
};

const StandardLayer = styled(animated.button)`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
  border: none;
  background: transparent;
`;
const FirstLayer = styled(animated.button)`
  display: block;
  position: relative;
  will-change: transform;
  border: none;
  background: transparent;
`;

const Label = styled.div`
  position: absolute;
  height: 48px;
  width: 1px;
  background: var(--color-text);
  transition: opacity 300ms;
  will-change: opacity;
`;

const TopLabel = styled(Label)`
  top: 0;
  left: 25%;
  transform: translateY(-100%);
`;

const LabelText = styled.div`
  position: absolute;
  transform: skewY(-20deg) translateX(-50%);
  width: max-content;
  color: var(--color-text);

  ${TopLabel} & {
    top: -32px;
    transform-origin: top left;
  }
`;

const Image = styled.img`
  position: relative;
  display: block;
  width: 100%;
`;

export default Layer;
